import appContext from 'context/appContext'
import { useContext, useState } from 'react'
import { checkRequestForClientIds, fetchCsApiData, getUrlFromEndpoint } from 'utils'
import { FEApiError } from 'utils/errorHandling'
import { AmbiguousURLType } from 'utils/utils.d'
import useOsContext from './useOsContext'
import { OsContextValue } from '@wppopen/react'

export type FetchReq<T> = (
  endpointOrUrl?: string | URL,
  fetchOptions?: Partial<RequestInit>,
  osContextValue?: OsContextValue | null | undefined,
) => Promise<T>

export interface ApiResponse<T> {
  fetchData: FetchReq<T>
  data: T | null
  isLoading: boolean
  error: FEApiError | null
  url: URL
  setUrl: React.Dispatch<React.SetStateAction<URL>>
  overrideErrorsHandling?: boolean
}
export default function useApi<T>(
  initialEndpointOrUrl: AmbiguousURLType,
  initialFetchOptions: Partial<RequestInit> = {},
  retries: number = 0,
  responseType: 'json' | 'csv' = 'json',
  overrideErrorsHandling = false,
): ApiResponse<T> {
  const [data, setData] = useState<T | null>(null)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [error, setError] = useState<FEApiError | null>(null)
  const [url, setUrl] = useState<URL>(getUrlFromEndpoint(initialEndpointOrUrl))

  const { osContextValue } = useContext(appContext)
  const { tenantId, account_ids, bearerHeader } = useOsContext({})

  // TODO: add back in once wpp open integration is complete
  // const osContext = useOs().osContext

  const fetchData = async (endpointOrUrl = initialEndpointOrUrl, fetchOptions = initialFetchOptions) => {
    setError(null)
    setIsLoading(true)
    const authToken = osContextValue?.osApi?.getAccessToken()

    if (!!authToken) {
      fetchOptions.headers = {
        ...fetchOptions.headers,
        ...bearerHeader,
      }
    }

    if (!!tenantId) {
      fetchOptions.headers = {
        ...fetchOptions.headers,
        ...account_ids,
      }
    }

    try {
      checkRequestForClientIds(endpointOrUrl, osContextValue?.osContext.baseUrl || '', fetchOptions)
      const resJson = await fetchCsApiData<T>(
        endpointOrUrl,
        fetchOptions,
        retries,
        responseType,
        overrideErrorsHandling,
      )
      setData(resJson)
      return resJson
    } catch (err) {
      setError(err as FEApiError)
      throw err
    } finally {
      setIsLoading(false)
    }
  }

  return { fetchData, data, isLoading, error, url, setUrl }
}
